import { fsaNeedsSetup, type FsaConfigForPreview } from "./fsaPreview";
import { isFsaPlanType } from "@shared/schemas/planTypes";

/** What we need to know about a plan's config to judge its enrollment status. */
export interface PlanConfigContext {
  fsaConfig: FsaConfigForPreview | null | undefined;
  /** Whether the plan has a row in plan_contributions. */
  hasContributions: boolean;
}

export interface PlanEnrollmentStatus {
  /** Employees won't see this plan during enrollment (a config gap hides it). */
  hiddenFromEmployees: boolean;
  /** The plan is fully set up for enrollment. */
  configReady: boolean;
  /** Why it's hidden / not ready — tooltip copy. */
  reason?: string;
}

const FSA_LIMITS_REASON =
  "FSA annual election limits must be set before employees can enroll. " +
  "This plan is hidden from employees until configured.";

/**
 * Single source for "can employees see this plan / is it set up for enrollment".
 * Shared by the broker plans list (Status + Plan State badges) and the employee
 * enrollment filter, so the two surfaces can't drift apart.
 *
 * Today the only config gap that *hides* a plan is an unconfigured FSA (matches
 * the employee filter). Readiness is type-aware: FSA needs election limits;
 * other plans need contributions. Structured so more hide reasons can be added.
 */
export function getPlanEnrollmentStatus(
  planType: string | null | undefined,
  ctx: PlanConfigContext,
): PlanEnrollmentStatus {
  const isFsa = isFsaPlanType(String(planType ?? ""));
  const hiddenFromEmployees = fsaNeedsSetup(planType, ctx.fsaConfig);
  const configReady = isFsa ? !hiddenFromEmployees : ctx.hasContributions;
  return {
    hiddenFromEmployees,
    configReady,
    reason: hiddenFromEmployees ? FSA_LIMITS_REASON : undefined,
  };
}
